import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Card, CardContent } from './ui/card';
import { Phone, Mail, Clock, CheckCircle2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const ContactInfoItem: React.FC<{
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}> = ({ icon, title, children }) => (
  <div className="flex items-start">
    <div className="flex-shrink-0 w-12 h-12 bg-yellow-400/20 text-yellow-300 rounded-xl flex items-center justify-center">
      {icon}
    </div>
    <div className="ml-4">
      <h3 className="text-lg font-bold text-white">{title}</h3>
      <div className="mt-1 text-blue-200">{children}</div>
    </div>
  </div>
);

const ContactForm: React.FC = () => {
  const { t } = useLanguage();
  
  // Generate random numbers for spam protection
  const generateMathQuestion = () => {
    const num1 = Math.floor(Math.random() * 10) + 1; // 1-10
    const num2 = Math.floor(Math.random() * 10) + 1; // 1-10
    return { num1, num2, answer: num1 + num2 };
  };

  const [mathQuestion, setMathQuestion] = useState(generateMathQuestion());
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    moveType: 'residential',
    moveFrom: '',
    moveTo: '',
    message: '',
    mathAnswer: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [mathError, setMathError] = useState('');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value: string) => {
    setFormData((prev) => ({ ...prev, moveType: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMathError('');

    // Validate math answer
    const userAnswer = parseInt(formData.mathAnswer, 10);
    if (isNaN(userAnswer) || userAnswer !== mathQuestion.answer) {
      setMathError(t('contactFormSpamError'));
      return;
    }

    setIsSubmitting(true);

    try {
      // Send form data to API
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          moveType: formData.moveType,
          moveFrom: formData.moveFrom,
          moveTo: formData.moveTo,
          message: formData.message,
          mathAnswer: formData.mathAnswer,
          mathNum1: mathQuestion.num1,
          mathNum2: mathQuestion.num2,
        }),
      });

      // Check if response is OK and try to parse JSON
      let data: any;
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        try {
          data = await response.json();
        } catch (jsonError) {
          // If JSON parsing fails, get text response
          const textResponse = await response.text();
          console.error('Failed to parse JSON response:', textResponse);
          throw new Error(`Server error: ${textResponse.substring(0, 100)}`);
        }
      } else {
        // Response is not JSON, get text
        const textResponse = await response.text();
        console.error('Non-JSON response received:', textResponse);
        throw new Error(`Server error: ${textResponse.substring(0, 100)}`);
      }

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Failed to send message');
      }

      // Success - reset form and show success message
      setIsSubmitting(false);
      setIsSubmitted(true);
      const newQuestion = generateMathQuestion();
      setMathQuestion(newQuestion);
      setFormData({
        name: '',
        email: '',
        phone: '',
        moveType: 'residential',
        moveFrom: '',
        moveTo: '',
        message: '',
        mathAnswer: '',
      });
      setMathError('');
      setTimeout(() => setIsSubmitted(false), 5000);
    } catch (error: any) {
      console.error('Error submitting form:', error);
      setIsSubmitting(false);
      setMathError(error.message || 'Failed to send message. Please try again or contact us directly.');
    }
  };

  return (
    <section
      id="contact"
      className="relative py-20 sm:py-24 bg-blue-900 bg-cover bg-center"
      style={{ backgroundImage: "url('/images/contact-bg.jpg')" }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/90 via-gray-900/80 to-blue-900/90"></div>

      <div className="container relative px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white">
            {t('contactTitle')}
          </h2>
          <p className="mt-4 max-w-3xl mx-auto text-xl text-blue-200">
            {t('contactSubtitle')}
          </p>
          <div className="mt-6 mx-auto w-24 h-1.5 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-full"></div>
        </div>

        <div className="grid lg:grid-cols-2 lg:gap-16">
          {/* Contact Info */}
          <div className="text-white mb-12 lg:mb-0">
            <h3 className="text-3xl font-bold mb-8">{t('contactInfo')}</h3>
            <div className="space-y-8">
              <ContactInfoItem
                icon={<Phone className="h-6 w-6" />}
                title={t('contactPhone')}
              >
                <p>0450 688 920</p>
                <p>0424 666 920</p>
              </ContactInfoItem>
              <ContactInfoItem icon={<Mail className="h-6 w-6" />} title={t('contactEmail')}>
                <p>michaelremovalists@gmail.com</p>
                <p>danlin0425@gmail.com</p>
              </ContactInfoItem>
              <ContactInfoItem icon={<Clock className="h-6 w-6" />} title={t('contactHours')}>
                <p>{t('contactHoursWeekday')}</p>
                <p>{t('contactHoursWeekend')}</p>
              </ContactInfoItem>
            </div>
          </div>

          {/* Form */}
          <div>
            <Card className="bg-white/10 backdrop-blur-md border-white/20 shadow-2xl">
              <CardContent className="p-8">
                {isSubmitted ? (
                  <div className="text-center py-10">
                    <div className="w-20 h-20 bg-green-500/20 text-green-400 rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle2 className="h-10 w-10" />
                    </div>
                    <h3 className="text-2xl font-bold text-white">{t('contactFormSuccess')}</h3>
                    <p className="mt-2 text-blue-200">{t('contactFormSuccessDesc')}</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-blue-200">
                          {t('contactFormName')}
                        </Label>
                        <Input
                          type="text"
                          name="name"
                          id="name"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          className="bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:ring-yellow-400"
                          placeholder={t('contactFormNamePlaceholder')}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-blue-200">
                          {t('contactFormPhone')}
                        </Label>
                        <Input
                          type="tel"
                          name="phone"
                          id="phone"
                          required
                          value={formData.phone}
                          onChange={handleChange}
                          className="bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:ring-yellow-400"
                          placeholder={t('contactFormPhonePlaceholder')}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-blue-200">
                        {t('contactFormEmail')}
                      </Label>
                      <Input
                        type="email"
                        name="email"
                        id="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:ring-yellow-400"
                        placeholder={t('contactFormEmailPlaceholder')}
                      />
                    </div>
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="moveFrom" className="text-blue-200">
                          {t('contactFormMoveFrom')}
                        </Label>
                        <Input
                          type="text"
                          name="moveFrom"
                          id="moveFrom"
                          value={formData.moveFrom}
                          onChange={handleChange}
                          className="bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:ring-yellow-400"
                          placeholder={t('contactFormMoveFromPlaceholder')}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="moveTo" className="text-blue-200">
                          {t('contactFormMoveTo')}
                        </Label>
                        <Input
                          type="text"
                          name="moveTo"
                          id="moveTo"
                          value={formData.moveTo}
                          onChange={handleChange}
                          className="bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:ring-yellow-400"
                          placeholder={t('contactFormMoveToPlaceholder')}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-blue-200">
                        {t('contactFormMessage')}
                      </Label>
                      <Textarea
                        name="message"
                        id="message"
                        rows={4}
                        value={formData.message}
                        onChange={handleChange}
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:ring-yellow-400"
                        placeholder={t('contactFormMessagePlaceholder')}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="mathAnswer" className="text-blue-200">
                        {t('contactFormSpam')}
                      </Label>
                      <div className="flex items-center gap-3">
                        <span className="text-white text-lg font-medium whitespace-nowrap">
                          {mathQuestion.num1} + {mathQuestion.num2} = ?
                        </span>
                        <Input
                          type="number"
                          name="mathAnswer"
                          id="mathAnswer"
                          required
                          value={formData.mathAnswer}
                          onChange={handleChange}
                          className={`bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:ring-yellow-400 ${
                            mathError ? 'border-red-400 focus:ring-red-400' : ''
                          }`}
                          placeholder="请输入答案"
                        />
                      </div>
                      {mathError && (
                        <p className="text-red-400 text-sm mt-1">{mathError}</p>
                      )}
                    </div>
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      size="lg"
                      className="w-full bg-yellow-400 hover:bg-yellow-300 text-blue-900 text-lg font-bold shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 disabled:bg-yellow-600 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? t('contactFormSubmitting') : t('contactFormSubmit')}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
